import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * CLICK-TO-OPEN CUSTOMISATION (K&F, 12 Jul 2026): dropdowns open on click, not
 * hover. The hover events wired in blocks/_header-menu.liquid (on:pointerenter /
 * on:pointerleave / on:focus) are ignored here so that file needs no changes.
 * First click on a parent item opens its dropdown; clicking it again, clicking
 * anywhere else, or pressing Escape closes it. Links inside dropdowns navigate
 * as normal. A ▾ caret is injected on parents that have a dropdown.
 * 13 Jul (Em ruling): hover effects in the header are disabled entirely — no
 * grey-out of siblings, no colour change, no underline on hover. The theme's
 * native colours are untouched. The only state marker is an underline on the
 * item whose dropdown is OPEN (click feedback, not hover).
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('click', this.#menuClickListener);
    document.addEventListener('click', this.#outsideClickListener);
    document.addEventListener('keydown', this.#escapeListener);
    this.#injectClickMenuStyles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('click', this.#menuClickListener);
    document.removeEventListener('click', this.#outsideClickListener);
    document.removeEventListener('keydown', this.#escapeListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Toggle a dropdown when its parent menu item is clicked.
   * Clicks on links inside an open dropdown are left alone so they navigate.
   * @param {MouseEvent} event
   */
  #menuClickListener = (event) => {
    if (!(event.target instanceof Element)) return;

    const listItem = event.target.closest('.menu-list__list-item');
    if (!listItem || !this.contains(listItem)) return;

    // Only intercept clicks on the top-level link/button itself
    const control = listItem.querySelector(':scope > a, :scope > button');
    if (!control || !control.contains(event.target)) return;

    const item = findMenuItem(listItem);
    if (!item) return;

    const isMoreButton = listItem.getAttribute('slot') === 'more';
    if (!isMoreButton && !findSubmenu(item)) return; // plain link, let it navigate

    event.preventDefault();

    if (item === this.#state.activeItem) {
      this.#deactivate(item, true);
    } else {
      this.activate({ target: listItem });
    }
  };

  /**
   * Close the open dropdown when clicking anywhere outside the menu
   * @param {MouseEvent} event
   */
  #outsideClickListener = (event) => {
    if (!this.#state.activeItem) return;
    if (event.composedPath().includes(this)) return;
    this.#deactivate(this.#state.activeItem, true);
  };

  /**
   * Close the open dropdown on Escape
   * @param {KeyboardEvent} event
   */
  #escapeListener = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
    this.#deactivate(this.#state.activeItem, true);
  };

  /**
   * Caret on parents that have a dropdown, so click-to-open is discoverable
   */
  #injectClickMenuStyles() {
    if (document.getElementById('click-menu-styles')) return;
    const style = document.createElement('style');
    style.id = 'click-menu-styles';
    style.textContent = `
      .menu-list__link[aria-haspopup='true'] .menu-list__link-title::after {
        content: ' ▾';
        display: inline-block;
        font-size: 0.8em;
        margin-inline-start: 0.15em;
        transition: transform 0.2s ease;
      }
      .menu-list__link[aria-haspopup='true'][aria-expanded='true'] .menu-list__link-title::after {
        transform: scaleY(-1);
      }
      /* K&F (Em ruling, 13 Jul): no hover effects in the header.
         1) The theme greys out sibling links on hover via this opacity
            variable — setting it to 1 disables the fade. */
      .menu-list {
        --opacity-subdued-text: 1;
      }
      /* Open-state marker only (click feedback, not hover) */
      .menu-list__link[aria-expanded='true'] .menu-list__link-title {
        text-decoration: underline;
        text-underline-offset: 5px;
        text-decoration-thickness: 1.5px;
      }
      /* K&F: close button in the top-right of an open dropdown */
      [ref='submenu[]'] {
        position: relative;
      }
      .bab-menu-close {
        position: absolute;
        top: 14px;
        inset-inline-end: 18px;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: 1.5px solid currentColor;
        background: transparent;
        color: inherit;
        font-size: 22px;
        line-height: 1;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        z-index: 2;
        opacity: 0.85;
      }
      .bab-menu-close:hover,
      .bab-menu-close:focus-visible {
        opacity: 1;
      }
      @media (max-width: 749px) {
        .bab-menu-close { display: none; }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * A visible × in the top-right of an open dropdown — clicking the header
   * item again also closes it, but the button makes closing obvious.
   * @param {HTMLElement} submenu
   */
  #ensureCloseButton(submenu) {
    if (submenu.querySelector('.bab-menu-close')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bab-menu-close';
    btn.setAttribute('aria-label', 'Close menu');
    btn.textContent = '×';
    btn.addEventListener('click', (event) => {
      event.stopPropagation();
      this.#deactivate(this.#state.activeItem, true);
    });
    submenu.appendChild(btn);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | { target: Element }} event
   */
  activate = (event) => {
    // Hover/focus no longer opens the menu — only explicit clicks (see #menuClickListener)
    if (event.type === 'pointerenter' || event.type === 'focus') return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      if (submenu !== this.overflowMenu) this.#ensureCloseButton(submenu);
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    // Moving the mouse away no longer closes the menu — clicks and Escape do
    if (event.type === 'pointerleave') return;

    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force] - skip the overflow-hover guard (used by click-toggle, Escape and outside-click)
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
